import * as React from 'react'

import { safeParseHtml } from '../../libs/safeHtml'
import { blockAncestorOf, isSpeakableReactElement, isSpeakableTextNode, spokenWordOf } from '../../libs/speech/speechText'
import { BLOCK_ELEMENTS, SpeechToken, WordCollector, createWordCollector } from '../../libs/speech/wordCollector'

interface KaraokeChunkProps {
  className?: string;
  content?: React.ReactNode;
  element?: 'h1' | 'h2' | 'p' | 'div';
  html?: string;
  id?: string;
  spokenWordIndex: number | null;
}

interface BuildSectionOptions {
  annotateBold?: boolean;
  content?: React.ReactNode;
  html?: string;
  spokenWordIndex: number | null;
}

interface WordWrapper {
  collector: WordCollector;
  nextKey: () => string;
  spokenWordIndex: number | null;
}

const beginBold = '<span class="sr-only">(begin bold) </span>'
const endBold = '<span class="sr-only"> (end bold)</span>'

const annotateForScreenreader = (html: string) => html.replace(/<strong>/g, `${beginBold}<strong>`).replace(/<\/strong>/g, `</strong>${endBold}`)

const createKeyCounter = () => {
  let count = 0
  return () => {
    count += 1
    return `read-aloud-${count}`
  }
}

const renderTokens = (tokens: SpeechToken[], spokenWordIndex: number | null, keyPrefix: string) => tokens.map((token, index) => {
  if (token.wordIndex === null) { return token.text }

  return (
    <span
      className={token.wordIndex === spokenWordIndex ? 'karaoke-highlight' : undefined}
      data-word-index={token.wordIndex}
      key={`${keyPrefix}-${index}`}
    >
      {token.text}
    </span>
  )
})

const wrapText = (text: string, wrapper: WordWrapper, keyPrefix: string) => renderTokens(wrapper.collector.tokenize(text), wrapper.spokenWordIndex, keyPrefix)

const parseHtmlWithWordSpans = (html: string, wrapper: WordWrapper) => {
  const enclosingBlock = wrapper.collector.currentBlock
  const replaceTextNode = (domNode: any) => {
    if (!isSpeakableTextNode(domNode)) { return undefined }

    wrapper.collector.setBlock(blockAncestorOf(domNode, enclosingBlock))
    return <span>{wrapText(domNode.data, wrapper, wrapper.nextKey())}</span>
  }
  const rendered = safeParseHtml(html, { replace: replaceTextNode, })
  wrapper.collector.setBlock(enclosingBlock)
  return rendered
}

const wrapSpokenWordElement = (node: React.ReactElement, spokenWord: string, wrapper: WordWrapper, keyPrefix: string) => {
  const wordIndexes = wrapper.collector.tokenize(spokenWord).map(token => token.wordIndex).filter(index => index !== null)
  const { className, } = node.props as { className?: string }
  const highlighted = wrapper.spokenWordIndex !== null && wordIndexes.includes(wrapper.spokenWordIndex)
  return React.cloneElement(node, {
    className: [className, highlighted ? 'karaoke-highlight' : null].filter(Boolean).join(' ') || undefined,
    'data-word-index': wordIndexes[0] ?? null,
    key: node.key ?? keyPrefix,
  })
}

const wrapNodeWords = (node: React.ReactNode, wrapper: WordWrapper, keyPrefix: string): React.ReactNode => {
  if (node == null || typeof node === 'boolean') { return node }
  if (typeof node === 'string' || typeof node === 'number') { return wrapText(String(node), wrapper, keyPrefix) }
  if (Array.isArray(node)) { return node.map((child, i) => wrapNodeWords(child, wrapper, `${keyPrefix}-${i}`)) }
  if (!React.isValidElement(node) || !isSpeakableReactElement(node)) { return node }

  const spokenWord = spokenWordOf(node)
  if (spokenWord) { return wrapSpokenWordElement(node, spokenWord, wrapper, keyPrefix) }

  const enclosingBlock = wrapper.collector.currentBlock
  if (typeof node.type === 'string' && BLOCK_ELEMENTS.includes(node.type)) { wrapper.collector.setBlock(node) }
  const wrapped = wrapElementChildren(node, wrapper, keyPrefix)
  wrapper.collector.setBlock(enclosingBlock)
  return wrapped
}

const wrapElementChildren = (node: React.ReactElement, wrapper: WordWrapper, keyPrefix: string): React.ReactNode => {
  const { children, dangerouslySetInnerHTML, ...rest } = node.props as { children?: React.ReactNode, dangerouslySetInnerHTML?: { __html?: string } } & Record<string, unknown>
  if (dangerouslySetInnerHTML?.__html) {
    return React.createElement(node.type, { ...rest, key: node.key ?? keyPrefix, }, parseHtmlWithWordSpans(dangerouslySetInnerHTML.__html, wrapper))
  }
  if (children == null) { return node }

  return React.cloneElement(node, { key: node.key ?? keyPrefix, }, wrapNodeWords(children, wrapper, `${keyPrefix}-c`))
}

const buildSection = ({ annotateBold, content, html, spokenWordIndex, }: BuildSectionOptions) => {
  const wrapper = { collector: createWordCollector(), nextKey: createKeyCounter(), spokenWordIndex, }
  const rendered = html != null
    ? parseHtmlWithWordSpans(annotateBold ? annotateForScreenreader(html) : html, wrapper)
    : wrapNodeWords(content, wrapper, 'read-aloud-node')
  return { rendered, words: wrapper.collector.finish(), }
}

const KaraokeChunk = React.memo(React.forwardRef<HTMLElement, KaraokeChunkProps & React.HTMLAttributes<HTMLElement>>(({ content, element = 'p', html, spokenWordIndex, ...rest }, ref) => {
  const { rendered, } = buildSection({ content, html, spokenWordIndex, })
  return React.createElement(element, { ...rest, ref, }, rendered)
}))

KaraokeChunk.displayName = 'KaraokeChunk'

export { KaraokeChunk, annotateForScreenreader, buildSection }
